import React from 'react';
import { ChevronLeft, Trash2, Mail, Database, Archive, Clock, CreditCard, PauseCircle, Smartphone } from 'lucide-react';
import { useNavigate } from "react-router-dom"
import { useStore } from '@tanstack/react-store';
import { themeStore } from '../../../stores/theme';

// TODO: replace with the real support inbox before submitting to App Store review
const SUPPORT_EMAIL = "support@pluggedin.app";

const STEPS = [
    "Open the PluggedIn app and sign in.",
    "Open the side drawer (menu).",
    <>Tap <strong>Deactivate Or Delete Account</strong>.</>,
    <>Select <strong>Delete account</strong>.</>,
    <>Optionally enter a reason in the <strong>Feedback</strong> box.</>,
    <>Tap <strong>Continue</strong> and confirm.</>,
];

const DETAILS = [
    {
        icon: Database,
        title: "What gets deleted",
        body: "Your profile (name, email, phone number, photo), content you created such as posts, events and messages, saved preferences, sign-in credentials and linked Apple or Google sign-in.",
    },
    {
        icon: Archive,
        title: "What we may keep",
        body: "Some records, such as payment and transaction history, may be retained where required by law (tax, accounting or fraud prevention), only for as long as that obligation lasts.",
    },
    {
        icon: Clock,
        title: "Timeline",
        body: "Deletion is permanent and cannot be undone. Your account is deactivated immediately and your data is removed from our systems within 30 days.",
    },
    {
        icon: CreditCard,
        title: "Subscriptions",
        body: "Deleting your account does not cancel subscriptions purchased through the App Store or Google Play. Cancel them first in your device's subscription settings.",
    },
];

const DeleteAccount = () => {
    const { isDarkMode } = useStore(themeStore)
    const navigate = useNavigate()

    const card = `${isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-orange-100"} rounded-3xl border shadow-lg`;
    const title = isDarkMode ? "text-white" : "text-gray-800";
    const text = isDarkMode ? "text-gray-300" : "text-gray-600";

    return (
        <div className={`min-h-screen ${isDarkMode ? "bg-gray-800" : "bg-gradient-to-b from-orange-50 to-orange-100"}`}>
            {/* Hero */}
            <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
                    <button
                        onClick={() => navigate(-1)}
                        aria-label="Go back"
                        className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors">
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <div className="text-center mt-4">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/20 mb-4">
                            <Trash2 className="w-8 h-8" />
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-bold">Delete Your Account</h1>
                        <p className="mt-3 text-white/90 max-w-2xl mx-auto">
                            You can permanently delete your PluggedIn account and its associated data at any time.
                            Choose either option below.
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 pb-16 space-y-8">
                {/* Options */}
                <div className="grid md:grid-cols-5 gap-6">
                    <div className={`${card} p-6 sm:p-8 md:col-span-3`}>
                        <div className="flex items-center gap-3 mb-6">
                            <Smartphone className="w-6 h-6 text-orange-500" />
                            <h2 className={`${title} text-xl font-semibold`}>Option 1: Delete from the app</h2>
                        </div>
                        <ol className="space-y-4">
                            {STEPS.map((step, i) => (
                                <li key={i} className="flex items-start gap-4">
                                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 to-red-600 text-white text-sm font-bold flex items-center justify-center">
                                        {i + 1}
                                    </span>
                                    <span className={`${text} pt-1`}>{step}</span>
                                </li>
                            ))}
                        </ol>
                    </div>

                    <div className={`${card} p-6 sm:p-8 md:col-span-2 flex flex-col`}>
                        <div className="flex items-center gap-3 mb-4">
                            <Mail className="w-6 h-6 text-orange-500" />
                            <h2 className={`${title} text-xl font-semibold`}>Option 2: Email us</h2>
                        </div>
                        <p className={`${text} flex-1`}>
                            Can't access the app? Email us from the address registered to your account with the subject
                            "Account Deletion Request". We may ask you to verify your identity before processing the request.
                        </p>
                        <a
                            href={`mailto:${SUPPORT_EMAIL}?subject=Account%20Deletion%20Request`}
                            className="mt-6 block text-center py-3 rounded-2xl bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold hover:opacity-90 transition-opacity">
                            Request Deletion
                        </a>
                    </div>
                </div>

                {/* Deactivate alternative */}
                <div className={`${isDarkMode ? "bg-orange-900/30 border-orange-800" : "bg-orange-50 border-orange-200"} border-l-4 border-l-orange-500 rounded-2xl p-6 flex items-start gap-4`}>
                    <PauseCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
                    <div>
                        <h3 className={`${title} font-semibold`}>Just need a break?</h3>
                        <p className={`${text} mt-1`}>
                            On the same screen choose <strong>Deactivate account</strong>. Deactivation is temporary: your
                            profile is hidden until you log in again. You can deactivate your account once a week.
                        </p>
                    </div>
                </div>

                {/* Details */}
                <div className="grid sm:grid-cols-2 gap-6">
                    {DETAILS.map(({ icon: Icon, title: heading, body }) => (
                        <div key={heading} className={`${card} p-6 hover:-translate-y-1 transition-transform`}>
                            <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mb-4">
                                <Icon className="w-6 h-6 text-orange-500" />
                            </div>
                            <h3 className={`${title} text-lg font-semibold mb-2`}>{heading}</h3>
                            <p className={`${text} text-sm leading-relaxed`}>{body}</p>
                        </div>
                    ))}
                </div>

                <p className={`${text} text-center`}>
                    Questions? Contact us at{' '}
                    <a href={`mailto:${SUPPORT_EMAIL}`} className="text-orange-500 font-medium underline">{SUPPORT_EMAIL}</a>{' '}
                    or read our <a href="/privacy" className="text-orange-500 font-medium underline">Privacy Policy</a>.
                </p>
            </div>
        </div>
    );
};

export default DeleteAccount;
